<i18n>
{
  "en": {
    "form": {
      "title": {
        "add": "Add user",
        "edit": "Edit user"
      },
      "fields": {
        "email": "Email",
        "firstname": "First name",
        "middlename": "Middle name",
        "lastname": "Last name",
        "country": "Country",
        "company": "Company",
        "permissions": "User permissions",
        "password": "Password",
        "repeatPassword": "Repeat password", 
        "referenceuser": "Reference user",
        "maxcredit": "Credit maximum",
        "mintime": "Time minimum",
        "maxtime": "Time maximum"
      },
      "options": {
        "permissions": {
          "select": "Please select a role",
          "ellipseadmin": "Ellipse Administrator",
          "ellipsetech": "Ellipse Technician",
          "creditdistributor": "Credit Distributor"
        }
      },
      "descriptions": {
        "optional": "(Optional)",
        "referenceuser": "Remote Assistance reference user",
        "days": "(Days)"
      },
      "texts": {
        "delete": "This will delete the user {email}. This action cannot be undone!"
      }
    },
    "buttons": {
      "save": "Save",
      "update": "Update",
      "add": "Add",
      "edit": "Edit",
      "delete": "Delete"
    }
  },
  "da": {
    "form": {
      "title": {
        "add": "Tilføj bruger",
        "edit": "Rediger bruger"
      },
      "fields": {
        "email": "Email",
        "firstname": "Fornavn",
        "middlename": "Mellemnavn",
        "lastname": "Efternavn",
        "country": "Land",
        "company": "Firma",
        "permissions": "Rettigheder", 
        "password": "Password",
        "repeatPassword": "Gentag password",
        "referenceuser": "Reference bruger",
        "maxcredit": "Credit maximum",
        "mintime": "Tids minimum",
        "maxtime": "Tids maximum"
      },
      "options": {
        "permissions": {
          "select": "Vælg venligst en rolle",
          "ellipseadmin": "Ellipse Administrator",
          "ellipsetech": "Ellipse Teknikker",
          "creditdistributor": "Kredit Distributør"
        }
      },
      "descriptions": {
        "optional": "(Valgfri)",
        "referenceuser": "Remote Assistance reference bruger",
        "days": "(Dage)"
      },
      "texts": {
        "delete": "Dette vil slette brugeren {email}. Denne handling kan ikke fortrydes!"
      }
    },
    "buttons": {
      "save": "Gem",
      "update": "Opdater",
      "add": "Tilføj",
      "edit": "Rediger",
      "delete": "Slet"
    }
  }
}
</i18n>

<template>
  
  <b-modal ref="userForm" hide-footer :title="modalAction === 'add' ? $t('form.title.add') : $t('form.title.edit')" no-close-on-backdrop 
  v-model="visible" >
    <b-form @submit.prevent="validateBeforeSubmit" onsubmit="return false">
      <b-form-group
        horizontal
        :label-cols="4"
        breakpoint="md"
        :label="$t('form.fields.email')">
        <b-form-input 
          name="email"
          v-model="user.Email"
          v-validate="'required|email'">
        </b-form-input>
        <span class="error" v-if="errors.has('email')">{{errors.first('email')}}</span>
      </b-form-group>

      <b-form-group
        horizontal
        :label-cols="4"
        breakpoint="md"
        :label="$t('form.fields.firstname')">
        <b-form-input 
          name="first_name"
          v-model="user.FirstName"
          v-validate="'required'">
        </b-form-input>
        <span class="error" v-if="errors.has('first_name')">{{errors.first('first_name')}}</span>
      </b-form-group>

      <b-form-group
        horizontal
        :label-cols="4"
        breakpoint="md"
        :label="$t('form.fields.middlename')"
        :description="$t('form.descriptions.optional')">
        <b-form-input v-model="user.MiddleName"></b-form-input>
      </b-form-group>

      <b-form-group
        horizontal
        :label-cols="4"
        breakpoint="md"
        :label="$t('form.fields.lastname')">
        <b-form-input 
          name="last_name"
          v-model="user.LastName"
          v-validate="'required'">
        </b-form-input>
        <span class="error" v-if="errors.has('last_name')">{{errors.first('last_name')}}</span>
      </b-form-group>

      <b-form-group
        horizontal
        :label-cols="4"
        breakpoint="md"
        :label="$t('form.fields.country')">
        <b-form-input 
          name="country"
          v-model="user.Country"
          v-validate="'required'">
        </b-form-input>
        <span class="error" v-if="errors.has('country')">{{errors.first('country')}}</span>
      </b-form-group>

      <b-form-group
        horizontal
        :label-cols="4"
        breakpoint="md"
        :label="$t('form.fields.company')">
        <b-form-input 
          name="company"
          v-model="user.CompanyName"
          v-validate="'required'">
        </b-form-input>
        <span class="error" v-if="errors.has('company')">{{errors.first('company')}}</span>
      </b-form-group>

      <b-form-group
        v-if="role === 'EllipseAdmin'"
        horizontal
        :label-cols="4"
        breakpoint="md"
        :label="$t('form.fields.permissions')">
        <b-form-select v-model="user.Role" class="mb-3" v-validate="{ required: true, is_not: undefined }" name="role">
          <option :value="undefined" disabled>{{ $t('form.options.permissions.select') }}</option>
          <option value="EllipseAdmin">{{ $t('form.options.permissions.ellipseadmin') }}</option>
          <option value="EllipseTech">{{ $t('form.options.permissions.ellipsetech') }}</option>
          <option value="CreditDistributor">{{ $t('form.options.permissions.creditdistributor') }}</option>
        </b-form-select>
        <span class="error" v-show="errors.has('role')">{{errors.first('role')}}</span>
      </b-form-group>

      <b-form-group
        horizontal
        :label-cols="4"
        breakpoint="md"
        :label="$t('form.fields.password')">
        <b-form-input 
          type="password"
          ref="password"
          name="password"
          v-model="user.Password"
          v-validate="(modalAction === 'add' ? 'required|' : '') + 'min:6|max:72|verify_password'">
        </b-form-input>
        <span class="error" v-if="errors.has('password')">{{errors.first('password')}}</span>
      </b-form-group>

      <b-form-group
        horizontal
        :label-cols="4"
        breakpoint="md"
        :label="$t('form.fields.repeatPassword')">
        <b-form-input 
          type="password"
          name="repeatPassword"
          v-validate="(modalAction === 'add' ? 'required|' : '') + 'confirmed:password'"
          v-model="user.repeatPassword">
        </b-form-input>
        <span class="error" v-if="errors.has('repeatPassword')">{{errors.first('repeatPassword')}}</span>
      </b-form-group>

      <div v-if="role === 'EllipseAdmin'">

        <hr />

        <b-form-group
          horizontal
          :label-cols="4"
          breakpoint="md"
          :label="$t('form.fields.referenceuser')"
          :description="$t('form.descriptions.referenceuser')">
          <b-form-input 
            name="referenceusername"
            v-model="user.ReferenceUser.ReferenceUsername"
            v-validate="'required|email'">
          </b-form-input>          
          <span class="error" v-if="errors.has('referenceusername')">{{errors.first('referenceusername')}}</span>
        </b-form-group>

        <hr />

        <b-form-group
          horizontal
          :label-cols="4"
          breakpoint="md"
          :label="$t('form.fields.maxcredit')">
          <b-form-input
            v-model="user.Limits.MaxCredits">
          </b-form-input>          
        </b-form-group>

        <b-form-group
          horizontal
          :label-cols="4"
          breakpoint="md"
          :label="$t('form.fields.mintime')"
          :description="$t('form.descriptions.days')">
          <b-form-input
            name="mintime"
            v-model="user.Limits.MinTime"
            v-validate="'min_value:1'"
            >
          </b-form-input>          
          <span class="error" v-if="errors.has('mintime')">{{errors.first('mintime')}}</span>
        </b-form-group>

        <b-form-group
          horizontal
          :label-cols="4"
          breakpoint="md"
          :label="$t('form.fields.maxtime')"
          :description="$t('form.descriptions.days')">
          <b-form-input
            name="maxtime"
            v-model="user.Limits.MaxTime"
            v-validate="`min_value:${minTimeValue}`"
            >
          </b-form-input>        
          <span class="error" v-if="errors.has('maxtime')">{{errors.first('maxtime')}}</span>  
        </b-form-group>

      </div>

      <b-button type="submit"  class="mt-3" variant="outline-success" block>
        {{
          modalAction === 'add' ? $t('buttons.save') : $t('buttons.update')
        }}
      </b-button>

    </b-form>
  </b-modal>
</template>

<script>

import roles from '../utils/roles.js'
import errorParser from '../utils/errorParser.js'
import elg from '../api/elg.gateway.js'
import jwt from '../utils/jwt.js'

export default {
  data() {
    return {
      _shown: false,
      /*user: {
        Role: undefined,
        ReferenceUser: {
          ReferenceUsername: ''
        }
      },*/
      originalReferenceUsername: undefined,
      showUserDialog: false
    }
  },
  methods: {
    validateBeforeSubmit(evt) {
      this.$validator.validateAll().then(result => {
        if (result) {
          this.onSubmit(evt);
        }
      });
    },
    onSubmit (evt) {
      evt.preventDefault();

      if(this.errors.any()){
        return;
      }
      
      if( this.modalAction === 'add' ){
        this.addUser();
      }else{
        this.editUser();
      }
    },
    addUser(){
      // Actual code
      this.user.RoleID = roles(this.user.Role);
      elg()
      .post('/api/v1/users', this.user)
      .then( res => {
        this.visible = false;        
        this.$emit('success', this.user)
      })
      .catch( err => {
        errorParser.parseErrors(this.$validator, this.$options, err);
      })
    },
    editUser(){
      // Actual code
      this.user.RoleID = roles(this.user.Role);
      elg()
      .put(`/api/v1/users/${this.user.UserID}`, this.user)
      .then( res => {
        this.visible = false;        

        if(this.user.UserID == jwt.getUserId()){
          // Self-update!
          console.info('Self-update!');
          if(this.originalReferenceUsername !== res.data.ReferenceUser.ReferenceUsername){
            location.reload();
          }
        }

        this.$emit('success', this.user)
      })
      .catch( err => {
        debugger;
        if(err.response.status === 404){
          // User has been removed in between... How to handle this?
          location.reload();
        }else{  
          errorParser.parseErrors(this.$validator, this.$options, err);
        }
      })
    },
  },
  sync: [ 'visible', 'user' ], //this is the special option added by the mixin
  props: [ 'success', 'modalAction' ],
  computed: {
    role() {
      return jwt.getRole()
    },
    minTimeValue(){
      if(this.user.Limits === undefined)
        return 1;
      if(this.user.Limits.MinTime === undefined)
        return 1;

      return this.user.Limits.MinTime
    }
  },
  watch: {
    visible: function(val){
      this.originalReferenceUsername = ((this.user||{}).ReferenceUser||{}).ReferenceUsername;
    }
  },
  mounted(){
    this.originalReferenceUsername = ((this.user||{}).ReferenceUser||{}).ReferenceUsername;
  }
}
</script>

<style scoped>
  .error{
    color: red;
    font-size: 12px;
  }
</style>




// WEBPACK FOOTER //
// WebApp/components/UserModal.vue